import { useOrganization } from '@/contexts/useOrganization'
import {
  Accordion,
  Box,
  Button,
  Field,
  HStack,
  NativeSelect,
  Switch,
  Table,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ExternalLink } from '@/components/ExternalLink/ExternalLink'
import prodConfig from '@/config/pulse-servers-prod.json'
import stagingConfig from '@/config/pulse-servers-staging.json'
import { isProduction } from '@/scripts/environments'
import type { PulseServer, PulseServersConfig } from '@/types/pulseServer'
import { FormLabelWithChangeIndicator } from './FormLabelWithChangeIndicator'

export type OrgFeaturesPulseProps = {
  onHasChangesChange: (hasChanges: boolean) => void
  onCancel: () => void
  resetChanges?: boolean
  canRead?: boolean
  canEdit?: boolean
}

export const OrgFeaturesPulse = ({
  onHasChangesChange,
  onCancel,
  resetChanges,
  canRead = true,
  canEdit = true,
}: OrgFeaturesPulseProps) => {
  const { t } = useTranslation('features')
  const orgContext = useOrganization()
  const { orgFeatures } = orgContext
  const { data: features, isLoading, isError } = orgFeatures

  const pulseServersConfig: PulseServersConfig = useMemo(
    () => (isProduction ? prodConfig : stagingConfig),
    [],
  )

  const availableServers = pulseServersConfig.pulseServers

  const [selectedServer, setSelectedServer] = useState<PulseServer | null>(null)

  const [editPulse, setEditPulse] = useState<{
    enabled: boolean
    pulseServerUrl: string
    pulseServerId: string
  } | null>(null)
  const [savedPulse, setSavedPulse] = useState<{
    enabled: boolean
    pulseServerUrl: string
    pulseServerId: string
  } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (features) {
      const initial = {
        enabled: Boolean(features.pulse?.enabled),
        pulseServerUrl: features.pulse?.pulseServerUrl || '',
        pulseServerId: features.pulse?.pulseServerId || '',
      }
      setEditPulse(initial)
      setSavedPulse(initial)

      // Find and set the selected server based on current config
      if (features.pulse?.pulseServerId) {
        const currentServer = availableServers.find(
          (server) => server.pulseServerId === features.pulse?.pulseServerId,
        )
        setSelectedServer(currentServer || null)
      }
    }
  }, [features, availableServers])

  useEffect(() => {
    if (resetChanges !== undefined && savedPulse) {
      setEditPulse({ ...savedPulse })
      // Reset selected server when canceling changes
      if (savedPulse.pulseServerId) {
        const currentServer = availableServers.find(
          (server) => server.pulseServerId === savedPulse.pulseServerId,
        )
        setSelectedServer(currentServer || null)
      }
    }
  }, [resetChanges, savedPulse, availableServers])

  useEffect(() => {
    if (!editPulse || !savedPulse) {
      setHasChanges(false)
      return
    }
    const enabledChanged = editPulse.enabled !== savedPulse.enabled
    const urlChanged = editPulse.pulseServerUrl !== savedPulse.pulseServerUrl
    const idChanged = editPulse.pulseServerId !== savedPulse.pulseServerId
    const newHasChanges = enabledChanged || urlChanged || idChanged
    setHasChanges(newHasChanges)
    onHasChangesChange(newHasChanges)
  }, [editPulse, savedPulse, onHasChangesChange])

  const handlePulseSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    setEditPulse((prev) =>
      prev
        ? {
            ...prev,
            enabled: e.target.checked,
          }
        : prev,
    )
  }

  const handleServerChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const serverId = e.target.value
    const server = availableServers.find((s) => s.pulseServerId === serverId)

    setSelectedServer(server || null)

    if (server) {
      setEditPulse((prev) =>
        prev
          ? {
              ...prev,
              pulseServerId: server.pulseServerId,
              pulseServerUrl: server.pulseServerUrl,
            }
          : prev,
      )
    }
  }

  function validateForm() {
    if (!editPulse || !selectedServer) {
      return false
    }
    return true
  }

  const handleSaveClick = () => {
    if (editPulse && selectedServer) {
      const formIsValid = validateForm()
      if (!formIsValid) {
        return
      }

      const pulseData = {
        enabled: editPulse.enabled,
        pulseServerUrl: selectedServer.pulseServerUrl,
        pulseServerId: selectedServer.pulseServerId,
      }

      orgContext
        .patchOrganisationFeatures({ pulse: { ...pulseData } })
        .then(() => {
          setSavedPulse({ ...pulseData })
          setHasChanges(false)
          onHasChangesChange(false)
        })
    }
  }

  const handleCancelClick = () => {
    onCancel()
  }

  const isControlEnabled = isLoading || isError || !canEdit

  const status = features?.pulse?.enabled
    ? t('organization.features.status.enabled')
    : t('organization.features.status.disabled')

  return (
    <Accordion.Item value="pulse" disabled={isLoading || isError || !canRead}>
      <>
        <Accordion.ItemTrigger>
          <Box
            as="span"
            flex="1"
            textAlign="left"
            display="flex"
            alignItems="center"
            gap={2}
          >
            {t('organization.features.pulse.title')}
            <Tag.Root
              size="sm"
              colorPalette={features?.pulse?.enabled ? 'green' : 'gray'}
              aria-label={status}
            >
              <Tag.Label>{status}</Tag.Label>
            </Tag.Root>
            {!canEdit && (
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Tag.Root size="sm" colorPalette="orange">
                    <Tag.Label>
                      {t('organization.features.readOnly.tag')}
                    </Tag.Label>
                  </Tag.Root>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    {t('organization.features.readOnly.tooltip')}
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            )}
            {hasChanges && (
              <Tag.Root
                size="sm"
                colorPalette="orange"
                ml={2}
                aria-label={String(
                  t('organization.features.unsavedChangesAria'),
                )}
              >
                <Tag.Label>
                  {t('organization.features.unsavedChanges')}
                </Tag.Label>
              </Tag.Root>
            )}
          </Box>
          <Accordion.ItemIndicator />
        </Accordion.ItemTrigger>
        <Accordion.ItemContent pb={4}>
          <VStack alignItems="start" gap={4} pl={4}>
            <Field.Root
              display="flex"
              alignItems="start"
              disabled={isControlEnabled}
            >
              <FormLabelWithChangeIndicator
                htmlFor="pulse-enabled"
                mb="0"
                showChangeIndicator={editPulse?.enabled !== savedPulse?.enabled}
              >
                {t('organization.features.status.enabled')}
              </FormLabelWithChangeIndicator>
              <Switch.Root
                checked={editPulse?.enabled}
                colorPalette={editPulse?.enabled ? 'green' : 'gray'}
                onCheckedChange={(details) =>
                  handlePulseSwitch({
                    target: { checked: details.checked },
                  } as ChangeEvent<HTMLInputElement>)
                }
              >
                <Switch.Control />
                <Switch.HiddenInput />
              </Switch.Root>
            </Field.Root>
            {editPulse?.enabled && (
              <Field.Root disabled={!canEdit}>
                <FormLabelWithChangeIndicator
                  showChangeIndicator={(() => {
                    if (!editPulse || !savedPulse) {
                      return false
                    }
                    return editPulse.pulseServerId !== savedPulse.pulseServerId
                  })()}
                >
                  {t('organization.features.pulse.title')} Server
                </FormLabelWithChangeIndicator>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={selectedServer?.pulseServerId || ''}
                    onChange={handleServerChange}
                  >
                    <option value="">
                      {t('organization.features.pulse.selectServer')}
                    </option>
                    {availableServers.map((server) => (
                      <option
                        key={server.pulseServerId}
                        value={server.pulseServerId}
                      >
                        {server.pulseServerUrl}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <Field.HelperText>
                  {t('organization.features.pulse.selectServerHelper')}
                </Field.HelperText>
              </Field.Root>
            )}

            {editPulse?.enabled && selectedServer && (
              <Box p={4} borderWidth={1} borderRadius="md">
                <Text fontWeight="bold" mb={3}>
                  {t('organization.features.pulse.serverDetails')}
                </Text>
                <Table.Root size="sm" variant="outline">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t('organization.features.pulse.property')}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t('organization.features.pulse.value')}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell fontWeight="medium">
                        {t('organization.features.pulse.pulseServerUrl')}
                      </Table.Cell>
                      <Table.Cell>
                        <ExternalLink href={selectedServer.pulseServerUrl}>
                          {selectedServer.pulseServerUrl}
                        </ExternalLink>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell fontWeight="medium">
                        {t('organization.features.pulse.pulseServerId')}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="sm">
                        {selectedServer.pulseServerId}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell fontWeight="medium">
                        {t('organization.features.pulse.agentDns')}
                      </Table.Cell>
                      <Table.Cell>{selectedServer.agentDns}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell fontWeight="medium">
                        {t('organization.features.pulse.apiary')}
                      </Table.Cell>
                      <Table.Cell>{selectedServer.apiary}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell fontWeight="medium">
                        {t('organization.features.pulse.provisioned')}
                      </Table.Cell>
                      <Table.Cell>
                        <Tag.Root
                          size="sm"
                          colorPalette={
                            selectedServer.provisioned ? 'green' : 'gray'
                          }
                        >
                          <Tag.Label>
                            {selectedServer.provisioned
                              ? t('organization.features.pulse.yes')
                              : t('organization.features.pulse.no')}
                          </Tag.Label>
                        </Tag.Root>
                      </Table.Cell>
                    </Table.Row>
                    {selectedServer.grafanaMetrics && (
                      <Table.Row>
                        <Table.Cell fontWeight="medium">
                          {t('organization.features.pulse.grafanaMetrics')}
                        </Table.Cell>
                        <Table.Cell>
                          <ExternalLink href={selectedServer.grafanaMetrics}>
                            {t('organization.features.pulse.viewMetrics')}
                          </ExternalLink>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
            {hasChanges && (
              <HStack gap={2} pt={2}>
                <Button
                  colorPalette="blue"
                  size="sm"
                  onClick={handleSaveClick}
                  disabled={isControlEnabled || !hasChanges}
                >
                  {t('organization.features.actions.save')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelClick}
                  disabled={isControlEnabled}
                >
                  {t('organization.features.actions.cancel')}
                </Button>
              </HStack>
            )}
          </VStack>
        </Accordion.ItemContent>
      </>
    </Accordion.Item>
  )
}
